import { Injectable } from '@angular/core';

import { Observable, combineLatest, distinctUntilChanged, filter, map, shareReplay, startWith } from 'rxjs';

import { v4 as uuid } from 'uuid';
import { HttpLink } from 'apollo-angular/http';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { ApolloClient, ApolloLink, InMemoryCache, NormalizedCacheObject, ObservableQuery, from } from '@apollo/client/core';
import { WatchQueryOptions } from 'apollo-angular';
import { TmtLoggerService } from 'tmt-logger';

import { environment } from 'src/environments/environment';
import { AuthService } from 'src/services/auth.service';
import { LaunchDarklyService } from 'src/services/launchdarkly.service';

import { ExternalSessionService } from './external-session.service';
import { ExternalModeService } from './external-mode.service';

/**
 * Service providing apollo client
 */
@Injectable({
	providedIn: 'root',
})
export class ApolloService {
	/**
	 * Observable of data which is common between all available clients.
	 */
	private commonClientConfiguration$ = this.authService.userData$.pipe(
		distinctUntilChanged((prev, curr) => {
			if (!prev.sessionID && !curr.sessionID) {
				return true;
			}
			return prev.sessionID === curr.sessionID;
		}),
		map(userData => {
			// Set headers for authentication and correlation.
			const headerLink = setContext((_, { headers }) => {
				this.loggerService.logDebug('setContext');
				const correlationId = uuid();
				const requestId = uuid();
				const sessionId = userData.sessionID || 'anonymous';
				this.loggerService.logDebug('graphql sessionId: ' + sessionId);

				return {
					headers: {
						...headers,
						'x-correlation-id': correlationId,
						'x-request-id': requestId,
						authorization: sessionId,
					},
				};
			});

			// Configure error handling.
			const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
				this.loggerService.logDebug('onError');
				const correlationId = operation.getContext()['headers']['x-correlation-id'];
				if (graphQLErrors) {
					graphQLErrors.forEach(({ message, locations, path }) => {
						this.loggerService.logError(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`, correlationId);
					});
				}

				if (networkError) {
					if (networkError.message.includes('401') || networkError.message.includes('403')) {
						// Also clear external session to ensure We don't have stale sessions.
						this.externalSessionService.clearSession();
						this.authService.clearSession();
					}
					this.loggerService.logError(`[Network error]: ${JSON.stringify(networkError)}`, correlationId);
				}
			});

			const timeStartLink = new ApolloLink((operation, forward) => {
				this.loggerService.logDebug('forward start');
				const correlationId = operation.getContext()['headers']['x-correlation-id'];
				const startTime = new Date();
				this.loggerService.logInformation(`[${operation.operationName}] Sending request`, correlationId);
				operation.setContext({ start: startTime });
				this.loggerService.logDebug('forward end');
				return forward(operation);
			});

			const timeEndLink = new ApolloLink((operation, forward) => {
				this.loggerService.logDebug('ApolloLink');
				return forward(operation).map(data => {
					const correlationId = operation.getContext()['headers']['x-correlation-id'];
					const duration = new Date().getTime() - operation.getContext()['start'].getTime();
					this.loggerService.logInformation(`[${operation.operationName}] took ${duration} to complete`, correlationId);
					return data;
				});
			});

			return {
				headerLink: headerLink,
				errorLink: errorLink,
				timeStartLink: timeStartLink,
				timeEndLink: timeEndLink,
			};
		}),
		shareReplay(1),
	);

	private internalAbstractionLayerClient$ = combineLatest({
		commonConfiguration: this.commonClientConfiguration$,
		isStaging: this.launchdarklyService.launchDarklyFlags$.pipe(
			map(flags => flags['staging']),
			distinctUntilChanged(),
		),
	}).pipe(
		map(source => {
			// Set uri based on environment from LaunchDarkly flags.
			const launchDarklyEnvironmentUri = source.isStaging ? environment.abstractionLayerStagingUrl : environment.abstractionLayerReleaseUrl;
			const uriLink = this.httpLink.create({ uri: launchDarklyEnvironmentUri });

			// Return client to subscribers.
			return new ApolloClient({
				link: from([source.commonConfiguration.errorLink, source.commonConfiguration.headerLink, source.commonConfiguration.timeStartLink, source.commonConfiguration.timeEndLink, uriLink]),
				cache: new InMemoryCache({
					typePolicies: {
						Delivery: {
							keyFields: ['id', 'version'],
						},
					},
				}),
				connectToDevTools: false,
			});
		}),
		filter(client => !!client),
		shareReplay(1),
	);

	private externalAbstractionLayerClient$ = combineLatest({
		commonConfiguration: this.commonClientConfiguration$,
		isStaging: this.launchdarklyService.launchDarklyFlags$.pipe(
			map(flags => flags['staging']),
			distinctUntilChanged(),
		),
		externalToken: this.externalSessionService.externalToken$.pipe(startWith(null)),
	}).pipe(
		map(source => {
			if (!source.externalToken) {
				return null;
			}

			const uri = source.isStaging ? environment.externalAbstractionLayerStagingUri : environment.externalAbstractionLayerReleaseUri;
			console.log(`[ApolloClientService] Building external client — uri=${uri}, staging=${source.isStaging}`);
			const uriLink = this.httpLink.create({ uri });

			const correlationId = uuid();
			const requestId = uuid();

			const externalHeaderLink = setContext((_, { headers }) => ({
				headers: {
					...headers,
					'x-correlation-id': correlationId,
					'x-request-id': requestId,
					Authorization: `Bearer ${source.externalToken}`,
				},
			}));

			const externalErrorLink = onError(({ graphQLErrors, networkError }) => {
				if (graphQLErrors) {
					graphQLErrors.forEach(({ message, path }) => console.error(`[ExternalApolloClient] GraphQL error: ${message}, path: ${path}`));
				}
				if (networkError) {
					console.error(`[ExternalApolloClient] Network error: ${JSON.stringify(networkError)}`);

					this.externalSessionService.clearSession();
				}
			});

			return new ApolloClient({
				link: from([externalErrorLink, externalHeaderLink, source.commonConfiguration.timeStartLink, source.commonConfiguration.timeEndLink, uriLink]),
				cache: new InMemoryCache(),
				defaultOptions: {
					query: {
						fetchPolicy: 'network-only',
						errorPolicy: 'all',
					},
				},
			});
		}),
		shareReplay(1),
	);

	/**
	 * Observable of apollo client for communicating with abstraction layer.
	 */
	public abstractionLayerClient$ = combineLatest({
		internalClient: this.internalAbstractionLayerClient$,
		externalClient: this.externalAbstractionLayerClient$.pipe(startWith(null)),
		isExternalMode: this.externalModeService.isExternalMode$,
	}).pipe(
		map(source => (source.isExternalMode && source.externalClient ? source.externalClient : source.internalClient)),
		shareReplay(1),
	);

	/**
	 * Observable of apollo client for communicating with abstraction layer.
	 */
	public appSyncDbClient$ = this.commonClientConfiguration$.pipe(
		map(source => {
			// Set uri based on environment from LaunchDarkly flags.
			const appSyncUri = '';
			const uriLink = this.httpLink.create({ uri: appSyncUri });

			// Return client to subscribers.
			return new ApolloClient({
				link: from([source.errorLink, source.headerLink, source.timeStartLink, source.timeEndLink, uriLink]),
				cache: new InMemoryCache(),
				connectToDevTools: false,
			});
		}),
		filter(client => !!client),
		shareReplay(1),
	);

	/**
	 * Observable of apollo client for communicating with abstraction layer.
	 */
	public authLayerClient = combineLatest({
		commonConfiguration: this.commonClientConfiguration$,
		isStaging: this.launchdarklyService.launchDarklyFlags$.pipe(
			map(flags => flags['staging']),
			distinctUntilChanged(),
		),
	}).pipe(
		map(source => {
			// Set uri based on environment from LaunchDarkly flags.
			const authLayerUri = source.isStaging ? environment.authLayerStagingUrl : environment.authLayerReleaseUrl;
			const uriLink = this.httpLink.create({ uri: authLayerUri });

			// Return client to subscribers.
			return new ApolloClient({
				link: from([source.commonConfiguration.errorLink, source.commonConfiguration.headerLink, source.commonConfiguration.timeStartLink, source.commonConfiguration.timeEndLink, uriLink]),
				cache: new InMemoryCache(),
			});
		}),
		filter(client => !!client),
		shareReplay(1),
	);

	/**
	 * Constructor.
	 * @param httpLink To create httpLink for client.
	 * @param sessionService To get session ID for authentication.
	 * @param authService
	 * @param launchdarklyService To get feature flags for current user.
	 * @param loggerService To log result and errors.
	 * @param externalSessionService
	 * @param externalModeService
	 */
	constructor(
		private httpLink: HttpLink,
		private authService: AuthService,
		private launchdarklyService: LaunchDarklyService,
		private loggerService: TmtLoggerService,
		private externalSessionService: ExternalSessionService,
		private externalModeService: ExternalModeService,
	) {}

	/**
	 * Watches a graphql query and returns an RxJS observable of that query.
	 * Since we use the ApolloClient API directly, rather than the apollo-angular abstraction,
	 * the client will return an ObservableQuery rather than an Observable.
	 * ObservableQuery is not directly compatible with RxJS.
	 * This function wraps watchQuery to convert that returned ObservableQuery to an RxJS Observable.
	 * @param client Client that performs the query.
	 * @param options Query options, as used by regular apollo query function.
	 * @returns An observable of the query result.
	 */
	public observableWatchQuery$(client: ApolloClient<NormalizedCacheObject>, options: WatchQueryOptions) {
		const query = client.watchQuery(options);

		return this.convertApolloObservableToRxJSObservable(query);
	}

	/**
	 * Converts an apollo ObservableQuery into an RxJS Observable.
	 * @param input ObservableQuery to subscribe to as an observable.
	 * @returns An RxJS observable matching the input.
	 */
	private convertApolloObservableToRxJSObservable(input: ObservableQuery<any>): Observable<any> {
		return new Observable(observer => {
			const subscription = input.subscribe({
				next: result => observer.next(result),
				error: error => observer.error(error),
				complete: () => observer.complete(),
			});

			return () => subscription.unsubscribe();
		});
	}
}
